import { DestroyRef, Directive, ElementRef, PLATFORM_ID, inject, input } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Drop-in double-click protection for any action button:
 *
 *   <button (click)="save()" appThrottleClick>Save</button>
 *   <button (click)="send()" [appThrottleClick]="3000">Send</button>
 *
 * The first click goes through; further clicks (and Enter-key implicit
 * submits, which arrive as clicks) are swallowed in the capture phase until
 * the lock expires. Blocking the click also blocks the form submit it would
 * have triggered.
 *
 * Client-side friction only – server-side limits are in functions/.
 */
@Directive({
  selector: '[appThrottleClick]',
})
export class ThrottleClickDirective {
  readonly throttleMs = input<number, number | string | undefined>(1500, {
    alias: 'appThrottleClick',
    transform: (v: number | string | undefined) => Number(v) || 1500,
  });

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private locked = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return;

    const host = this.el.nativeElement;
    const listener = (event: Event) => {
      if (this.locked) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return;
      }
      this.locked = true;
      host.setAttribute('aria-busy', 'true');
      this.timer = setTimeout(() => {
        this.locked = false;
        host.removeAttribute('aria-busy');
      }, this.throttleMs());
    };

    // Capture phase so we run before the (click) handler bound in the template.
    host.addEventListener('click', listener, true);
    inject(DestroyRef).onDestroy(() => {
      host.removeEventListener('click', listener, true);
      if (this.timer) clearTimeout(this.timer);
    });
  }
}
